import {
  BranchRuleKind,
  BranchTargetKind,
  RatingOperator,
} from '@/contracts/enums/branching';
import {
  BooleanBranchRules,
  BranchRules,
  BranchRuleTarget,
  MATRIX_ANY_ROW,
  MatrixBranchRules,
  RatingBranchCondition,
  RatingBranchRules,
} from '@/contracts/models/survey-branching';
import { EditorQuestion } from '@/contracts/models/survey-editor';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import React from 'react';
import { BranchTargetDropdown } from './BranchTargetDropdown';

export interface BranchRulesEditorProps {
  question: EditorQuestion;
  rules: BranchRules;
  questionNumber: number;
  isLastQuestion: boolean;
  onRulesChange: (rules: BranchRules) => void;
  disabled?: boolean;
}

const RATING_OPERATOR_LABEL_KEY: Record<RatingOperator, string> = {
  [RatingOperator.GreaterThan]: 'editor.branching.ratingOperators.greaterThan',
  [RatingOperator.LessThan]: 'editor.branching.ratingOperators.lessThan',
  [RatingOperator.GreaterOrEqual]:
    'editor.branching.ratingOperators.greaterOrEqual',
  [RatingOperator.LessOrEqual]: 'editor.branching.ratingOperators.lessOrEqual',
  [RatingOperator.Equal]: 'editor.branching.ratingOperators.equal',
  [RatingOperator.NotEqual]: 'editor.branching.ratingOperators.notEqual',
};

const MAX_RATING_CONDITIONS = 4;

/** Immutably patch the item at `index`. */
const replaceAt = <T,>(items: T[], index: number, patch: Partial<T>): T[] =>
  items.map((item, i) => (i === index ? { ...item, ...patch } : item));

/** Immutably drop the item at `index`. */
const removeAt = <T,>(items: T[], index: number): T[] =>
  items.filter((_, i) => i !== index);

/**
 * Rule editor for boolean, rating and matrix branching. Choice-based
 * questions configure targets inline on each choice row instead.
 */
export const BranchRulesEditor: React.FC<BranchRulesEditorProps> = ({
  question,
  rules,
  questionNumber,
  isLastQuestion,
  onRulesChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const branchChildren = question.branchChildren ?? [];

  const targetDropdown = (
    value: BranchRuleTarget | undefined,
    onChange: (target: BranchRuleTarget) => void,
    ariaLabel: string
  ) => (
    <BranchTargetDropdown
      value={value}
      onChange={onChange}
      branchChildren={branchChildren}
      parentNumber={questionNumber}
      isLastQuestion={isLastQuestion}
      disabled={disabled}
      ariaLabel={ariaLabel}
    />
  );

  const renderBooleanRules = (booleanRules: BooleanBranchRules) => (
    <div className='space-y-2'>
      <div className='flex items-center gap-3'>
        <span className='text-sm text-gray-600 w-24'>
          {question.labelTrue || t('editor.questionTypes.booleanYes')}
        </span>
        {targetDropdown(
          booleanRules.trueTarget,
          target => onRulesChange({ ...booleanRules, trueTarget: target }),
          t('editor.branching.yesTargetAriaLabel')
        )}
      </div>
      <div className='flex items-center gap-3'>
        <span className='text-sm text-gray-600 w-24'>
          {question.labelFalse || t('editor.questionTypes.booleanNo')}
        </span>
        {targetDropdown(
          booleanRules.falseTarget,
          target => onRulesChange({ ...booleanRules, falseTarget: target }),
          t('editor.branching.noTargetAriaLabel')
        )}
      </div>
    </div>
  );

  const renderRatingRules = (ratingRules: RatingBranchRules) => {
    const operatorOptions = Object.values(RatingOperator).map(operator => ({
      label: t(RATING_OPERATOR_LABEL_KEY[operator]),
      value: operator,
    }));

    // Constrain the comparison value to the question's rating scale so authors
    // can't type a number outside it (which would silently never match).
    const ratingMin = question.rateMin ?? 1;
    const ratingMax = question.rateMax ?? 20;
    // Rating scales are small (validation caps the max at 20). Clamp the option
    // count so a transient out-of-range rateMax can't build a giant array.
    const optionCount = Math.min(Math.max(0, ratingMax - ratingMin + 1), 100);
    const valueOptions = Array.from({ length: optionCount }, (_, i) => ({
      label: String(ratingMin + i),
      value: ratingMin + i,
    }));

    const updateCondition = (
      index: number,
      patch: Partial<RatingBranchCondition>
    ) =>
      onRulesChange({
        ...ratingRules,
        conditions: replaceAt(ratingRules.conditions, index, patch),
      });

    const removeCondition = (index: number) =>
      onRulesChange({
        ...ratingRules,
        conditions: removeAt(ratingRules.conditions, index),
      });

    const addCondition = () => {
      onRulesChange({
        ...ratingRules,
        conditions: [
          ...ratingRules.conditions,
          {
            operator: RatingOperator.GreaterOrEqual,
            value: question.rateMin ?? 1,
            target: { kind: BranchTargetKind.NextQuestion },
          },
        ],
      });
    };

    return (
      <div className='space-y-2'>
        {ratingRules.conditions.map((condition, index) => (
          <div
            key={index}
            className='flex flex-wrap items-center gap-2 text-sm text-gray-600'
          >
            <span>{t('editor.branching.ifAnswer')}</span>
            <Dropdown
              value={condition.operator}
              options={operatorOptions}
              onChange={e => updateCondition(index, { operator: e.value })}
              disabled={disabled}
              aria-label={t('editor.branching.conditionOperatorAriaLabel', {
                number: index + 1,
              })}
              className='flex-shrink-0'
              pt={{ input: { className: 'py-2 px-2 text-sm' } }}
            />
            <Dropdown
              value={condition.value}
              options={valueOptions}
              onChange={e =>
                updateCondition(index, { value: e.value ?? condition.value })
              }
              disabled={disabled}
              aria-label={t('editor.branching.conditionValueAriaLabel', {
                number: index + 1,
              })}
              className='flex-shrink-0'
              pt={{ input: { className: 'py-2 px-2 text-sm' } }}
            />
            {targetDropdown(
              condition.target,
              target => updateCondition(index, { target }),
              t('editor.branching.conditionTargetAriaLabel', {
                number: index + 1,
              })
            )}
            <Button
              icon='pi pi-minus-circle'
              rounded
              text
              severity='danger'
              onClick={() => removeCondition(index)}
              disabled={disabled || ratingRules.conditions.length <= 1}
              aria-label={t('editor.branching.removeConditionAriaLabel', {
                number: index + 1,
              })}
              className='flex-shrink-0 !p-1'
            />
          </div>
        ))}
        <Button
          icon='pi pi-plus-circle'
          text
          size='small'
          label={t('editor.branching.addCondition')}
          onClick={addCondition}
          disabled={
            disabled || ratingRules.conditions.length >= MAX_RATING_CONDITIONS
          }
          aria-label={t('editor.branching.addConditionAriaLabel')}
          className='!p-1'
        />
      </div>
    );
  };

  const renderMatrixRules = (matrixRules: MatrixBranchRules) => {
    const rows = question.rows ?? [];
    const columns = question.columns ?? [];
    // The row is picked once for the whole rule; "Any row" matches the value in
    // any row. Conditions below only vary the value.
    const rowOptions = [
      { label: t('editor.branching.anyRow'), value: MATRIX_ANY_ROW },
      ...rows.map((row, index) => ({
        label:
          row.text ||
          t('editor.fields.rowPlaceholderNumbered', { number: index + 1 }),
        value: row.value,
      })),
    ];
    const columnOptions = columns.map((column, index) => ({
      label:
        column.text ||
        t('editor.fields.columnPlaceholderNumbered', { number: index + 1 }),
      value: column.value,
    }));

    const updateCondition = (
      index: number,
      patch: Partial<MatrixBranchRules['conditions'][number]>
    ) =>
      onRulesChange({
        ...matrixRules,
        conditions: replaceAt(matrixRules.conditions, index, patch),
      });

    const removeCondition = (index: number) =>
      onRulesChange({
        ...matrixRules,
        conditions: removeAt(matrixRules.conditions, index),
      });

    const addCondition = () =>
      onRulesChange({
        ...matrixRules,
        conditions: [
          ...matrixRules.conditions,
          {
            column: columns[0]?.value ?? '',
            target: { kind: BranchTargetKind.NextQuestion },
          },
        ],
      });

    const canAddCondition = matrixRules.conditions.length < columns.length;

    return (
      <div className='flex flex-wrap items-start gap-3 text-sm text-gray-600'>
        <div className='flex items-center gap-2 flex-shrink-0'>
          <span>{t('editor.branching.ifRow')}</span>
          <Dropdown
            value={matrixRules.row}
            options={rowOptions}
            onChange={e => onRulesChange({ ...matrixRules, row: e.value })}
            disabled={disabled}
            aria-label={t('editor.branching.matrixRowAriaLabel')}
            className='w-40'
            pt={{ input: { className: 'py-2 px-2 text-sm' } }}
          />
        </div>
        <div className='space-y-2'>
          {matrixRules.conditions.map((condition, index) => (
            <div key={index} className='flex flex-wrap items-center gap-2'>
              <span>{t('editor.branching.isValue')}</span>
              <Dropdown
                value={condition.column}
                options={columnOptions}
                onChange={e => updateCondition(index, { column: e.value })}
                disabled={disabled}
                aria-label={t('editor.branching.cellColumnAriaLabel', {
                  number: index + 1,
                })}
                className='w-40 flex-shrink-0'
                pt={{ input: { className: 'py-2 px-2 text-sm' } }}
              />
              {targetDropdown(
                condition.target,
                target => updateCondition(index, { target }),
                t('editor.branching.cellTargetAriaLabel', { number: index + 1 })
              )}
              <Button
                icon='pi pi-minus-circle'
                rounded
                text
                severity='danger'
                onClick={() => removeCondition(index)}
                disabled={disabled}
                aria-label={t('editor.branching.removeCellAriaLabel', {
                  number: index + 1,
                })}
                className='flex-shrink-0 !p-1'
              />
            </div>
          ))}
          {canAddCondition && (
            <Button
              icon='pi pi-plus-circle'
              text
              size='small'
              label={t('editor.branching.addCell')}
              onClick={addCondition}
              disabled={disabled}
              aria-label={t('editor.branching.addCellAriaLabel')}
              className='!p-1'
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className='mt-4 p-3 rounded-md bg-blue-50 border border-blue-100'>
      <div className='text-sm font-medium text-gray-700 mb-2'>
        {t('editor.branching.rulesTitle')}
      </div>
      {rules.kind === BranchRuleKind.Boolean && renderBooleanRules(rules)}
      {rules.kind === BranchRuleKind.Rating && renderRatingRules(rules)}
      {rules.kind === BranchRuleKind.Matrix && renderMatrixRules(rules)}
    </div>
  );
};
